import { useCallback, useEffect, useState } from 'react'

import { Xmark } from '@gravity-ui/icons'
import { Button, InputGroup } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { DriverAutocomplete } from '@/components/common/DriverAutocomplete'
import { Card, CardContent } from '@/components/ui/Card'
import { useDebounce } from '@/hooks/useDebounce'
import type { Driver } from '@/types/driver'

interface SettlementsFiltersProps {
  driverID: string;
  phone: string;
  onDriverChange: (val: string) => void;
  onPhoneChange: (val: string) => void;
}

export function SettlementsFilters({
  driverID,
  phone,
  onDriverChange,
  onPhoneChange,
}: SettlementsFiltersProps) {
  const { t } = useTranslation()

  // Adjust state when props change (Render-time adjustment pattern)
  const [prevPhone, setPrevPhone] = useState(phone)
  const [phoneVal, setPhoneVal] = useState(phone)
  if (phone !== prevPhone) {
    setPrevPhone(phone)
    setPhoneVal(phone)
  }

  const debouncedPhone = useDebounce(phoneVal, 400)

  useEffect(() => {
    if (debouncedPhone !== phone) {
      onPhoneChange(debouncedPhone)
    }
  }, [debouncedPhone, phone, onPhoneChange])

  const handlePhoneInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneVal(e.target.value)
  }, [])

  const handleClearPhone = useCallback(() => {
    setPhoneVal('')
  }, [])

  const handleDriverChange = useCallback((d: Driver | null) => {
    onDriverChange(d?.ID ?? '')
  }, [onDriverChange])

  return (
    <Card>
      <CardContent className="p-2">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <DriverAutocomplete
              selectedId={driverID}
              onSelectionChange={handleDriverChange}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <InputGroup className="w-full" variant="secondary">
              <InputGroup.Input
                value={phoneVal}
                onChange={handlePhoneInputChange}
                placeholder={t('drivers.phone')}
                className="w-full"
              />
              {phoneVal && (
                <InputGroup.Suffix>
                  <Button
                    isIconOnly
                    aria-label="Clear"
                    size="sm"
                    variant="ghost"
                    onClick={handleClearPhone}
                  >
                    <Xmark className="size-3" />
                  </Button>
                </InputGroup.Suffix>
              )}
            </InputGroup>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
